import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import type { EmployeeDetail, EmploymentType, Sex } from '@ip/shared-types';
import { EMPLOYMENT_TYPES, parsePesel } from '@ip/shared-types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/shared/DatePicker';
import { cn } from '@/lib/utils';
import { ContractorPicker } from './ContractorPicker';
import { t } from '@/i18n';
import { validateIban } from '@/utils/iban';

const ID_DOC_TYPES = ['dowod', 'passport'] as const;
const ID_DOC_NONE = '__none__';
const SEXES = ['male', 'female'] as const;
const SEX_NONE = '__none__';

export interface EmployeeFormValues {
  firstName: string;
  lastName: string;
  employmentType: EmploymentType;
  pesel?: string;
  /** Derived from the PESEL when one is entered (the backend re-derives it anyway). */
  dateOfBirth?: string;
  /** Derived from the PESEL when one is entered; manual otherwise. */
  sex?: Sex;
  idDocumentType?: 'dowod' | 'passport';
  idDocumentNumber?: string;
  taxOffice?: string;
  nfzBranch?: string;
  privateEmail?: string;
  privatePhone?: string;
  street?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  privateIban?: string;
  contractStartDate?: string;
  contractExpiryDate?: string;
  notes?: string;
  contractor?: { contractorId?: string; contractorNip?: string } | null;
}

/** RHF-internal shape: dates as Date objects (shared DatePicker works with Date | null). */
type RhfValues = Omit<
  EmployeeFormValues,
  'dateOfBirth' | 'contractStartDate' | 'contractExpiryDate'
> & {
  dateOfBirth?: Date | null;
  contractStartDate?: Date | null;
  contractExpiryDate?: Date | null;
};

function toRhfValues(initialValues?: EmployeeDetail | null): RhfValues {
  if (!initialValues) {
    return {
      firstName: '',
      lastName: '',
      employmentType: undefined as unknown as EmploymentType,
      pesel: '',
      dateOfBirth: null,
      sex: undefined,
      idDocumentType: undefined,
      idDocumentNumber: '',
      taxOffice: '',
      nfzBranch: '',
      privateEmail: '',
      privatePhone: '',
      street: '',
      postalCode: '',
      city: '',
      country: 'Polska',
      privateIban: '',
      contractStartDate: null,
      contractExpiryDate: null,
      notes: '',
      contractor: null,
    };
  }
  return {
    firstName: initialValues.firstName,
    lastName: initialValues.lastName,
    // null = auto-provisioned employee — force HR to pick a type before saving.
    employmentType: initialValues.employmentType ?? (undefined as unknown as EmploymentType),
    pesel: initialValues.pesel ?? '',
    dateOfBirth: initialValues.dateOfBirth ? new Date(initialValues.dateOfBirth) : null,
    sex: initialValues.sex ?? undefined,
    idDocumentType: initialValues.idDocumentType ?? undefined,
    idDocumentNumber: initialValues.idDocumentNumber ?? '',
    taxOffice: initialValues.taxOffice ?? '',
    nfzBranch: initialValues.nfzBranch ?? '',
    privateEmail: initialValues.privateEmail ?? '',
    privatePhone: initialValues.privatePhone ?? '',
    street: initialValues.street ?? '',
    postalCode: initialValues.postalCode ?? '',
    city: initialValues.city ?? '',
    country: initialValues.country ?? 'Polska',
    privateIban: initialValues.privateIban ?? '',
    contractStartDate: initialValues.contractStartDate ? new Date(initialValues.contractStartDate) : null,
    contractExpiryDate: initialValues.contractExpiryDate ? new Date(initialValues.contractExpiryDate) : null,
    notes: initialValues.notes ?? '',
    contractor: initialValues.contractor ? { contractorId: initialValues.contractor.id } : null,
  };
}

/** Same output as the previous parseFormValues: dates formatted YYYY-MM-DD, undefined when unset. */
function toFormValues(values: RhfValues): EmployeeFormValues {
  return {
    ...values,
    dateOfBirth: values.dateOfBirth ? dayjs(values.dateOfBirth).format('YYYY-MM-DD') : undefined,
    contractStartDate: values.contractStartDate
      ? dayjs(values.contractStartDate).format('YYYY-MM-DD')
      : undefined,
    contractExpiryDate: values.contractExpiryDate
      ? dayjs(values.contractExpiryDate).format('YYYY-MM-DD')
      : undefined,
  };
}

interface FieldProps {
  label: string;
  htmlFor?: string;
  error?: string;
  help?: string;
  className?: string;
  children: React.ReactNode;
}

function Field({ label, htmlFor, error, help, className, children }: FieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error && <p className="text-destructive text-xs">{error}</p>}
      {help && <p className="text-muted-foreground text-xs">{help}</p>}
    </div>
  );
}

interface EmployeeFormProps {
  companyNip: string;
  initialValues?: EmployeeDetail | null;
  onSubmit: (values: EmployeeFormValues) => Promise<void>;
  disabled?: boolean;
  formId: string;
}

export function EmployeeForm({
  companyNip,
  initialValues,
  onSubmit,
  disabled,
  formId,
}: EmployeeFormProps) {
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RhfValues>({ defaultValues: toRhfValues(initialValues) });

  // Parity with the previous setFieldsValue effect: re-sync when initialValues changes.
  useEffect(() => {
    if (initialValues) {
      reset(toRhfValues(initialValues));
    }
  }, [initialValues, reset]);

  // PESEL is the source of truth for date of birth + sex (same parser the backend
  // uses — libs/shared-types/src/pesel.ts). Typing a valid one fills both fields
  // live and locks them; clearing the PESEL hands them back to the user.
  const peselValue = (watch('pesel') ?? '').trim();
  const peselParsed = peselValue ? parsePesel(peselValue) : null;
  const peselDerived = peselParsed?.valid ? peselParsed : null;
  // Shown live while typing — RHF would only surface its own error after a
  // submit attempt, and an unfixable PESEL should be visible right away.
  const peselLiveError =
    peselParsed && !peselParsed.valid
      ? t(`employees.validation.pesel.${peselParsed.errorCode}`)
      : undefined;

  useEffect(() => {
    if (!peselDerived?.dateOfBirth || !peselDerived.sex) return;
    setValue('dateOfBirth', new Date(`${peselDerived.dateOfBirth}T00:00:00`), {
      shouldDirty: true,
    });
    setValue('sex', peselDerived.sex, { shouldDirty: true });
  }, [peselDerived?.dateOfBirth, peselDerived?.sex, setValue]);

  const employmentType: EmploymentType = watch('employmentType') ?? 'employment_contract';
  const requireExpiry =
    employmentType === 'mandate_contract' || employmentType === 'mandate_contract_student';
  const isContractor = employmentType === 'contractor';

  return (
    <form id={formId} onSubmit={handleSubmit((values) => onSubmit(toFormValues(values)))}>
      <fieldset disabled={disabled} className="min-w-0">
        <h3 className="text-base font-semibold">{t('employees.employment.tab')}</h3>
        <div className="mt-2 grid grid-cols-24 gap-4">
          <Field
            label={t('employees.employment.type')}
            error={errors.employmentType?.message}
            className="col-span-8"
          >
            <Controller
              name="employmentType"
              control={control}
              rules={{ required: t('employees.validation.typeRequired') }}
              render={({ field }) => (
                <Select
                  value={field.value ?? ''}
                  onValueChange={(v) => field.onChange(v as EmploymentType)}
                  disabled={disabled}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EMPLOYMENT_TYPES.map((tp) => (
                      <SelectItem key={tp} value={tp}>
                        {t(`employees.type.${tp}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field label={t('employees.employment.contractStartDate')} className="col-span-8">
            <Controller
              name="contractStartDate"
              control={control}
              render={({ field }) => (
                <DatePicker value={field.value ?? null} onChange={field.onChange} />
              )}
            />
          </Field>
          <Field
            label={t('employees.employment.contractExpiryDate')}
            error={errors.contractExpiryDate?.message}
            className="col-span-8"
          >
            <Controller
              name="contractExpiryDate"
              control={control}
              rules={{
                validate: (value) =>
                  !requireExpiry || Boolean(value) || t('employees.validation.contractExpiryRequired'),
              }}
              render={({ field }) => (
                <DatePicker value={field.value ?? null} onChange={field.onChange} />
              )}
            />
          </Field>
        </div>

        {isContractor && (
          <Field
            label={t('employees.employment.contractor.title')}
            error={errors.contractor?.message}
            className="mt-4"
          >
            <Controller
              name="contractor"
              control={control}
              rules={{
                validate: (value) =>
                  !isContractor || value != null || t('employees.validation.contractorRequired'),
              }}
              render={({ field }) => (
                <ContractorPicker
                  companyNip={companyNip}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={disabled}
                />
              )}
            />
          </Field>
        )}

        <h3 className="mt-6 text-base font-semibold">{t('employees.identity.tab')}</h3>
        <div className="mt-2 grid grid-cols-24 gap-4">
          <Field
            label={t('employees.identity.firstName')}
            htmlFor={`${formId}-firstName`}
            error={errors.firstName?.message}
            className="col-span-12"
          >
            <Input
              id={`${formId}-firstName`}
              {...register('firstName', {
                required: t('employees.validation.firstNameRequired'),
              })}
            />
          </Field>
          <Field
            label={t('employees.identity.lastName')}
            htmlFor={`${formId}-lastName`}
            error={errors.lastName?.message}
            className="col-span-12"
          >
            <Input
              id={`${formId}-lastName`}
              {...register('lastName', {
                required: t('employees.validation.lastNameRequired'),
              })}
            />
          </Field>
          <Field
            label={t('employees.identity.pesel')}
            htmlFor={`${formId}-pesel`}
            error={errors.pesel?.message ?? peselLiveError}
            help={t('employees.identity.peselHelp')}
            className="col-span-8"
          >
            <Input
              id={`${formId}-pesel`}
              maxLength={11}
              inputMode="numeric"
              {...register('pesel', {
                validate: (value) => {
                  const pesel = (value ?? '').trim();
                  if (!pesel) return true; // optional: foreigners / B2B have no PESEL
                  const parsed = parsePesel(pesel);
                  return parsed.valid || t(`employees.validation.pesel.${parsed.errorCode}`);
                },
              })}
            />
          </Field>
          <Field
            label={t('employees.identity.dateOfBirth')}
            help={peselDerived ? t('employees.identity.derivedFromPesel') : undefined}
            className="col-span-8"
          >
            <Controller
              name="dateOfBirth"
              control={control}
              render={({ field }) => (
                <DatePicker
                  value={field.value ?? null}
                  onChange={field.onChange}
                  disabled={disabled || Boolean(peselDerived)}
                />
              )}
            />
          </Field>
          <Field
            label={t('employees.identity.sex')}
            help={peselDerived ? t('employees.identity.derivedFromPesel') : undefined}
            className="col-span-8"
          >
            <Controller
              name="sex"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value ?? ''}
                  onValueChange={(v) =>
                    field.onChange(v === SEX_NONE ? undefined : (v as Sex))
                  }
                  disabled={disabled || Boolean(peselDerived)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SEX_NONE}>{t('common.none')}</SelectItem>
                    {SEXES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {t(`employees.identity.sexValue.${s}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field label={t('employees.identity.idDocumentType')} className="col-span-6">
            <Controller
              name="idDocumentType"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value ?? ''}
                  onValueChange={(v) =>
                    field.onChange(v === ID_DOC_NONE ? undefined : (v as 'dowod' | 'passport'))
                  }
                  disabled={disabled}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ID_DOC_NONE}>{t('common.none')}</SelectItem>
                    {ID_DOC_TYPES.map((dt) => (
                      <SelectItem key={dt} value={dt}>
                        {t(`employees.identity.idDocument.${dt}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field
            label={t('employees.identity.idDocumentNumber')}
            htmlFor={`${formId}-idDocumentNumber`}
            className="col-span-6"
          >
            <Input id={`${formId}-idDocumentNumber`} {...register('idDocumentNumber')} />
          </Field>
          <Field
            label={t('employees.identity.taxOffice')}
            htmlFor={`${formId}-taxOffice`}
            className="col-span-12"
          >
            <Input id={`${formId}-taxOffice`} {...register('taxOffice')} />
          </Field>
          <Field
            label={t('employees.identity.nfzBranch')}
            htmlFor={`${formId}-nfzBranch`}
            className="col-span-12"
          >
            <Input id={`${formId}-nfzBranch`} {...register('nfzBranch')} />
          </Field>
          <Field
            label={t('employees.identity.privateEmail')}
            htmlFor={`${formId}-privateEmail`}
            className="col-span-12"
          >
            <Input id={`${formId}-privateEmail`} type="email" {...register('privateEmail')} />
          </Field>
          <Field
            label={t('employees.identity.privatePhone')}
            htmlFor={`${formId}-privatePhone`}
            className="col-span-12"
          >
            <Input id={`${formId}-privatePhone`} {...register('privatePhone')} />
          </Field>
        </div>

        <h3 className="mt-6 text-base font-semibold">{t('employees.identity.address')}</h3>
        <div className="mt-2 grid grid-cols-24 gap-4">
          <Field
            label={t('employees.identity.street')}
            htmlFor={`${formId}-street`}
            className="col-span-12"
          >
            <Input id={`${formId}-street`} {...register('street')} />
          </Field>
          <Field
            label={t('employees.identity.postalCode')}
            htmlFor={`${formId}-postalCode`}
            className="col-span-4"
          >
            <Input id={`${formId}-postalCode`} {...register('postalCode')} />
          </Field>
          <Field
            label={t('employees.identity.city')}
            htmlFor={`${formId}-city`}
            className="col-span-5"
          >
            <Input id={`${formId}-city`} {...register('city')} />
          </Field>
          <Field
            label={t('employees.identity.country')}
            htmlFor={`${formId}-country`}
            className="col-span-3"
          >
            <Input id={`${formId}-country`} {...register('country')} />
          </Field>
        </div>

        <Field
          label={t('employees.identity.privateIban')}
          htmlFor={`${formId}-privateIban`}
          error={errors.privateIban?.message}
          help={t('employees.identity.privateIbanHelp')}
          className="mt-4"
        >
          <Input
            id={`${formId}-privateIban`}
            {...register('privateIban', {
              validate: (value) => {
                if (!value) return true;
                const r = validateIban(value);
                return r.valid ? true : r.error ?? t('employees.validation.ibanInvalid');
              },
            })}
          />
        </Field>

        <Field
          label={t('employees.identity.notes')}
          htmlFor={`${formId}-notes`}
          className="mt-4"
        >
          <Textarea id={`${formId}-notes`} rows={3} {...register('notes')} />
        </Field>
      </fieldset>
    </form>
  );
}
